import React from 'react';
import { Select, DatePicker } from 'syngenta-digital-cropwise-react-ui-kit';
import { InputNumber, InputNumberProps } from 'antd';
import { Moment } from 'moment';
import { FormikErrors, FormikTouched } from 'formik';

import {
  FormValues,
  PersonCompany
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import LayoutStrings from 'screens/Applications&WorkOrders/StepsLayout/Constants/layoutStrings';
import { applicationMethodOptions } from 'screens/Applications&WorkOrders/StepsLayout/Constants/selectOptions';
import {
  COL_SPAN_NONE,
  COL_SPAN_ONE_THIRD
} from 'components/FormGrid/constants';
import { FormRowConfig } from 'components/FormGrid';
import getDropdownParentWithoutDetaching from 'screens/Applications&WorkOrders/StepsLayout/Helpers/getDropdownParentWithoutDetaching';
import EquipmentDropdown from 'components/EquipmentDropdown';
import { EquipmentItem } from 'components/EquipmentDropdown/types';
import MultiFormatDateTimePicker from 'components/MultiFormatDateTimePicker';
import AuthorizedBySelect from './components/AuthorizedBySelect';
import './index.css';

const FULL_WIDTH_STYLE = { width: '100%' };
const GROW_WIDTH_BASED_ON_CONTENT_STYLE = { minWidth: '373.3px' };
const LIST_HEIGHT_LARGE = 252;
const LIST_HEIGHT_MEDIUM = 192;

// Display format for date-time fields
const DISPLAY_DATETIME_FORMAT = 'MM-DD-YYYY hh:mmA';

interface FieldBuilderProps {
  values: FormValues;
  errors: FormikErrors<FormValues>;
  touched: FormikTouched<FormValues>;
  today: Moment;
  defaultStartTime: Moment;
  defaultEndTime: Moment;
  showChevron: (key: keyof DropdownState) => React.ReactNode;
  handleTimeChange: (
    field: 'startDateTime' | 'endDateTime',
    value: Moment | null
  ) => void;
  handleBlur: (e: any) => void;
  setFieldValue: (field: string, value: any) => void;
  handleDropdownVisibleChange: (
    open: boolean,
    field: keyof DropdownState
  ) => void;
  handleEquipmentChange: (
    value: EquipmentItem | EquipmentItem[] | null
  ) => void;
  getEquipmentValue: () => EquipmentItem[] | null;
  renderSelectOptions: (
    options: { value: string; label: string }[]
  ) => React.ReactElement[];
  preventDecimalInput: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onApplicationMethodChange?: (value: string) => void;
}

interface DropdownState {
  timingEvent: boolean;
  workType: boolean;
  authorizedBy: boolean;
  applicationMethod: boolean;
  equipment: boolean;
}

const handleApplicationMethodChange = (
  setFieldValue: (field: string, value: any) => void,
  onApplicationMethodChange?: (value: string) => void
) => (value: string) => {
  setFieldValue('applicationMethod', value);
  onApplicationMethodChange?.(value);
};

export const buildWorkOrderFields = (
  props: FieldBuilderProps
): FormRowConfig[] => {
  const {
    values,
    errors,
    touched,
    today,
    defaultStartTime,
    showChevron,
    handleTimeChange,
    handleBlur,
    setFieldValue,
    handleDropdownVisibleChange,
    handleEquipmentChange,
    getEquipmentValue,
    renderSelectOptions
  } = props;

  return [
    {
      id: 'datetime-and-authorization-row',
      fields: [
        {
          id: 'startDateTime',
          label: LayoutStrings.fields.startDateTime,
          colSpan: COL_SPAN_ONE_THIRD,
          required: true,
          error:
            touched.startDateTime && typeof errors.startDateTime === 'string'
              ? errors.startDateTime
              : undefined,
          render: () => (
            <MultiFormatDateTimePicker
              data-testid="start-date-time-picker"
              name="startDateTime"
              showTime
              allowClear={false}
              format={DISPLAY_DATETIME_FORMAT}
              placeholder={LayoutStrings.placeholders.startDateTime}
              style={FULL_WIDTH_STYLE}
              value={values.startDateTime || defaultStartTime}
              onChange={(value: Moment | null) =>
                handleTimeChange('startDateTime', value)
              }
              onBlur={() => handleBlur({ target: { name: 'startDateTime' } })}
              getPopupContainer={getDropdownParentWithoutDetaching}
            />
          )
        },
        {
          id: 'authorizationDate',
          label: LayoutStrings.fields.authorizationDate,
          colSpan: COL_SPAN_ONE_THIRD,
          required: true,
          error:
            touched.authorizationDate &&
            typeof errors.authorizationDate === 'string'
              ? errors.authorizationDate
              : undefined,
          render: () => (
            <DatePicker
              name="authorizationDate"
              format="MM-DD-YYYY"
              style={FULL_WIDTH_STYLE}
              value={values.authorizationDate ?? today}
              onChange={(value: Moment | null) =>
                setFieldValue('authorizationDate', value ?? today)
              }
              onBlur={() =>
                handleBlur({ target: { name: 'authorizationDate' } })
              }
              allowClear={false}
              getPopupContainer={getDropdownParentWithoutDetaching}
            />
          )
        },
        {
          id: 'authorizedBy',
          label: LayoutStrings.fields.authorizedBy,
          colSpan: COL_SPAN_ONE_THIRD,
          error: touched.authorizedBy ? errors.authorizedBy : undefined,
          render: () => (
            <AuthorizedBySelect
              style={FULL_WIDTH_STYLE}
              listHeight={LIST_HEIGHT_LARGE}
              value={values.authorizedBy}
              suffixIcon={showChevron('authorizedBy')}
              onChange={(value: PersonCompany | null) => {
                setFieldValue('authorizedBy', value);
              }}
              onBlur={() => handleBlur({ target: { name: 'authorizedBy' } })}
              onDropdownVisibleChange={(open: boolean) =>
                handleDropdownVisibleChange(open, 'authorizedBy')
              }
            />
          )
        }
      ]
    },
    {
      id: 'application-method-and-equipment-row',
      rowClassName: 'application-method-and-equipment-row',
      fields: [
        {
          id: 'applicationMethod',
          label: LayoutStrings.fields.applicationMethod,
          colSpan: COL_SPAN_ONE_THIRD,
          error: touched.applicationMethod
            ? errors.applicationMethod
            : undefined,
          render: () => (
            <Select
              data-testid="application-method-select"
              style={FULL_WIDTH_STYLE}
              showSearch
              value={values.applicationMethod}
              onChange={handleApplicationMethodChange(
                setFieldValue,
                props.onApplicationMethodChange
              )}
              onBlur={() =>
                handleBlur({ target: { name: 'applicationMethod' } })
              }
              showArrow
              allowClear={!!values.applicationMethod}
              className="custom-select"
              listHeight={LIST_HEIGHT_MEDIUM}
              suffixIcon={showChevron('applicationMethod')}
              onDropdownVisibleChange={(open: boolean) =>
                handleDropdownVisibleChange(open, 'applicationMethod')
              }
              getPopupContainer={getDropdownParentWithoutDetaching}
            >
              {renderSelectOptions(applicationMethodOptions)}
            </Select>
          )
        },
        {
          id: 'equipments',
          label: LayoutStrings.fields.equipment,
          colSpan: COL_SPAN_NONE,
          render: () => (
            <div
              style={GROW_WIDTH_BASED_ON_CONTENT_STYLE}
              className="equipment-select"
            >
              <EquipmentDropdown
                id="equipment"
                title="Create New Equipment"
                bottomButtonText="Create New Equipment"
                onChange={handleEquipmentChange}
                value={getEquipmentValue()}
                allowSearch
                hideIcon
                mode="multiple"
              />
            </div>
          )
        }
      ]
    }
  ];
};

const formatterInputNumber: InputNumberProps<number>['formatter'] = value => {
  return value ? `${value}`.replaceAll(/\B(?=(\d{3})+(?!\d))/g, ',') : '';
};

const parserInputNumber = (value: string | undefined) => {
  return ((value?.replaceAll(',', '') || '') as unknown) as number;
};

export const buildApplicationFields = (
  props: FieldBuilderProps
): FormRowConfig[] => {
  const {
    values,
    errors,
    touched,
    today,
    defaultStartTime,
    defaultEndTime,
    showChevron,
    handleTimeChange,
    handleBlur,
    setFieldValue,
    handleDropdownVisibleChange,
    handleEquipmentChange,
    getEquipmentValue,
    renderSelectOptions,
    preventDecimalInput
  } = props;

  return [
    {
      id: 'datetime-row',
      fields: [
        {
          id: 'startDateTime',
          label: LayoutStrings.fields.startDateTime,
          colSpan: COL_SPAN_ONE_THIRD,
          required: true,
          error:
            touched.startDateTime && typeof errors.startDateTime === 'string'
              ? errors.startDateTime
              : undefined,
          render: () => (
            <MultiFormatDateTimePicker
              data-testid="start-date-time-picker"
              name="startDateTime"
              showTime
              allowClear={false}
              format={DISPLAY_DATETIME_FORMAT}
              placeholder={LayoutStrings.placeholders.startDateTime}
              style={FULL_WIDTH_STYLE}
              value={values.startDateTime || defaultStartTime}
              onChange={(value: Moment | null) =>
                handleTimeChange('startDateTime', value)
              }
              onBlur={() => handleBlur({ target: { name: 'startDateTime' } })}
              getPopupContainer={getDropdownParentWithoutDetaching}
            />
          )
        },
        {
          id: 'endDateTime',
          label: LayoutStrings.fields.endDateTime,
          colSpan: COL_SPAN_ONE_THIRD,
          required: true,
          error:
            touched.endDateTime && typeof errors.endDateTime === 'string'
              ? errors.endDateTime
              : undefined,
          render: () => (
            <MultiFormatDateTimePicker
              data-testid="end-date-time-picker"
              name="endDateTime"
              showTime={{ use12Hours: true, format: 'hh:mm A' }}
              allowClear={false}
              format={DISPLAY_DATETIME_FORMAT}
              placeholder={LayoutStrings.placeholders.endDateTime}
              style={FULL_WIDTH_STYLE}
              value={values.endDateTime || defaultEndTime}
              onChange={(value: Moment | null) =>
                handleTimeChange('endDateTime', value)
              }
              onBlur={() => handleBlur({ target: { name: 'endDateTime' } })}
              getPopupContainer={getDropdownParentWithoutDetaching}
            />
          )
        },
        {
          id: 'duration',
          label: LayoutStrings.fields.duration,
          colSpan: COL_SPAN_ONE_THIRD,
          required: true,
          error: errors.durationHours,
          render: () => {
            return (
              <div className="flex gap-2">
                <InputNumber<number>
                  data-testid="duration-hours-input"
                  className="w-full operation-duration-hours-input"
                  prefixCls="syt-antd-input-number"
                  name="durationHours"
                  placeholder="2"
                  size="middle"
                  min={0}
                  step={1}
                  precision={0}
                  decimalSeparator=""
                  value={values.durationHours}
                  status={errors.durationHours ? 'error' : undefined}
                  onChange={value => {
                    if (
                      values.startDateTime
                        .clone()
                        .add(value ?? 0, 'hours')
                        .isValid()
                    ) {
                      setFieldValue('durationHours', value ?? 0);
                    }
                  }}
                  onBlur={handleBlur}
                  formatter={formatterInputNumber}
                  parser={parserInputNumber}
                  suffix={
                    <span className="text-neutral-60">
                      hour{values.durationHours === 1 ? '' : 's'}
                    </span>
                  }
                  onKeyDown={preventDecimalInput}
                />
                <InputNumber
                  data-testid="duration-minutes-input"
                  className="w-full operation-duration-minutes-input"
                  prefixCls="syt-antd-input-number"
                  name="durationMinutes"
                  placeholder="0"
                  size="middle"
                  min={0}
                  max={59}
                  step={15}
                  precision={0}
                  decimalSeparator=""
                  value={values.durationMinutes}
                  status={errors.durationMinutes ? 'error' : undefined}
                  onChange={value =>
                    setFieldValue('durationMinutes', value ?? 0)
                  }
                  onBlur={handleBlur}
                  formatter={formatterInputNumber}
                  parser={parserInputNumber}
                  suffix={<span className="text-neutral-60">min</span>}
                  onKeyDown={preventDecimalInput}
                />
              </div>
            );
          }
        }
      ]
    },
    {
      id: 'authorization-row',
      fields: [
        {
          id: 'authorizationDate',
          label: LayoutStrings.fields.authorizationDate,
          colSpan: COL_SPAN_ONE_THIRD,
          required: true,
          error:
            touched.authorizationDate &&
            typeof errors.authorizationDate === 'string'
              ? errors.authorizationDate
              : undefined,
          render: () => (
            <DatePicker
              name="authorizationDate"
              format="MM-DD-YYYY"
              style={FULL_WIDTH_STYLE}
              value={values.authorizationDate ?? today}
              onChange={(value: Moment | null) =>
                setFieldValue('authorizationDate', value ?? today)
              }
              onBlur={() =>
                handleBlur({ target: { name: 'authorizationDate' } })
              }
              allowClear={false}
              getPopupContainer={getDropdownParentWithoutDetaching}
            />
          )
        },
        {
          id: 'authorizedBy',
          label: LayoutStrings.fields.authorizedBy,
          colSpan: COL_SPAN_ONE_THIRD,
          error: touched.authorizedBy ? errors.authorizedBy : undefined,
          render: () => (
            <AuthorizedBySelect
              style={FULL_WIDTH_STYLE}
              listHeight={LIST_HEIGHT_LARGE}
              value={values.authorizedBy}
              suffixIcon={showChevron('authorizedBy')}
              onChange={(value: PersonCompany | null) => {
                setFieldValue('authorizedBy', value);
              }}
              onBlur={() => handleBlur({ target: { name: 'authorizedBy' } })}
              onDropdownVisibleChange={(open: boolean) =>
                handleDropdownVisibleChange(open, 'authorizedBy')
              }
            />
          )
        },
        {
          id: 'applicationMethod',
          label: LayoutStrings.fields.applicationMethod,
          colSpan: COL_SPAN_ONE_THIRD,
          error: touched.applicationMethod
            ? errors.applicationMethod
            : undefined,
          render: () => (
            <Select
              data-testid="application-method-select"
              style={FULL_WIDTH_STYLE}
              showSearch
              value={values.applicationMethod}
              onChange={handleApplicationMethodChange(
                setFieldValue,
                props.onApplicationMethodChange
              )}
              onBlur={() =>
                handleBlur({ target: { name: 'applicationMethod' } })
              }
              showArrow
              allowClear={!!values.applicationMethod}
              className="custom-select"
              listHeight={LIST_HEIGHT_MEDIUM}
              suffixIcon={showChevron('applicationMethod')}
              onDropdownVisibleChange={(open: boolean) =>
                handleDropdownVisibleChange(open, 'applicationMethod')
              }
              getPopupContainer={getDropdownParentWithoutDetaching}
            >
              {renderSelectOptions(applicationMethodOptions)}
            </Select>
          )
        }
      ]
    },
    {
      id: 'equipment-row',
      fields: [
        {
          id: 'equipments',
          label: LayoutStrings.fields.equipment,
          colSpan: COL_SPAN_NONE,
          render: () => (
            <div
              style={GROW_WIDTH_BASED_ON_CONTENT_STYLE}
              className="equipment-select"
            >
              <EquipmentDropdown
                id="equipment"
                title="Create New Equipment"
                bottomButtonText="Create New Equipment"
                onChange={handleEquipmentChange}
                value={getEquipmentValue()}
                allowSearch
                hideIcon
                mode="multiple"
              />
            </div>
          )
        }
      ]
    }
  ];
};
